import * as React from 'react'
import { getVariantConfig } from '../../appVariant'

// Step 1 of the upgrade dialog: the three plans from config/variants.js
// (ids + monthly amounts), annual by default. Bullets mirror the comparison
// modal's headline rows; "Compare all features" opens the full table.
const PLAN_BULLETS = {
    basic: [
        'Unlimited graphs',
        'Unlimited AI chats',
        'Files up to 1 MB, PDFs up to 2 MB',
    ],
    advanced: [
        'Everything in Basic',
        'Files up to 5 MB',
        'Live updates and scheduled imports',
    ],
    premium: [
        'Everything in Advanced',
        'Files up to 10 MB, PDFs up to 50 MB',
        'Custom branding',
    ],
}

const PLAN_LABELS = { basic: 'Basic', advanced: 'Advanced', premium: 'Premium' }

// The few numbers a buyer scans down to see the plans climb. `hero` is the one
// that most defines the tier (upload size — the only one enforced in code);
// `anchor` rolls up the tiers below so higher cards don't repeat them. Values
// are the comparison table's, except upload sizes, which come from the enforced
// caps in lib/planEntitlements.js.
const PLAN_HIGHLIGHTS = {
    basic: {
        anchor: null,
        hero: 'Files up to 1 MB, PDFs up to 2 MB',
        features: [
            'Batch upload up to 2 files',
            '40 AI credits / hour',
            '40 YouTube · 40 Google results per search',
            'Limited API & MCP (unlimited on annual)',
        ],
    },
    advanced: {
        anchor: 'Everything in Basic, plus:',
        hero: 'Files & PDFs up to 5 MB',
        features: [
            'Batch upload up to 20 files',
            '80 AI credits / hour',
            '100 YouTube · 60 Google results per search',
            'Limited API & MCP (unlimited on annual)',
        ],
    },
    premium: {
        anchor: 'Everything in Advanced, plus:',
        hero: 'PDFs up to 50 MB, files up to 10 MB',
        features: [
            'Batch upload up to 50 files',
            '200 AI credits / hour',
            '1000 YouTube · 100 Google results per search',
            'Unlimited API & MCP access',
        ],
    },
}

// `planPrices` comes from Chargebee through /billing/config and wins whenever
// it is there: the variant's `planAmounts` is a published list that can drift
// from what is actually charged, in amount and in currency. An annual item
// price is the whole year, so the row shows a twelfth of it.
export function monthlyPriceOf(price) {
    if (!price || typeof price.amount !== 'number') return null
    return {
        amount: price.periodUnit === 'year' ? price.amount / 12 : price.amount,
        currency: price.currency,
    }
}

export function getPlanOptions(period, planPrices = null) {
    const { tiers = {}, planAmounts = {} } = getVariantConfig().plans || {}
    return ['basic', 'advanced', 'premium']
        .map(key => {
            const planId =
                tiers[`${key}${period === 'annual' ? 'Annual' : 'Monthly'}`]
            const monthlyId = tiers[`${key}Monthly`]
            const annualId = tiers[`${key}Annual`]
            if (!planId) return null
            const quoted = planPrices && monthlyPriceOf(planPrices[planId])
            const quotedMonthly =
                planPrices && monthlyPriceOf(planPrices[monthlyId])
            const quotedAnnual =
                planPrices && monthlyPriceOf(planPrices[annualId])
            const amount = quoted ? quoted.amount / 100 : planAmounts[planId]
            const monthlyAmount = quotedMonthly
                ? quotedMonthly.amount
                : planAmounts[monthlyId]
            const annualAmount = quotedAnnual
                ? quotedAnnual.amount
                : planAmounts[annualId]
            const saving =
                monthlyAmount && annualAmount
                    ? Math.round((1 - annualAmount / monthlyAmount) * 100)
                    : null
            return {
                key,
                planId,
                label: PLAN_LABELS[key],
                amount,
                currency: quoted ? quoted.currency : null,
                saving,
                bullets: PLAN_BULLETS[key],
                highlights: PLAN_HIGHLIGHTS[key],
            }
        })
        .filter(Boolean)
}

export function PeriodToggle({ period, onChange, saving }) {
    const button = (value, label) => (
        <button
            type="button"
            onClick={() => onChange(value)}
            aria-pressed={period === value}
            className={`tw-rounded-md tw-border-0 tw-px-3 tw-py-1 tw-text-sm tw-font-medium tw-cursor-pointer ${
                period === value
                    ? 'tw-bg-background tw-text-foreground tw-shadow-sm'
                    : 'tw-bg-transparent tw-text-muted-foreground'
            }`}
        >
            {label}
        </button>
    )
    return (
        <div
            className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-lg tw-bg-muted tw-p-1"
            data-testid="plan-period-toggle"
        >
            {button('monthly', 'Monthly')}
            {button('annual', saving ? `Annual · save ${saving}%` : 'Annual')}
        </div>
    )
}

export default function PlanCards({
    period,
    selectedKey,
    onSelect,
    currentPlanId = null,
}) {
    const options = React.useMemo(() => getPlanOptions(period), [period])
    if (options.length === 0) {
        return (
            <p className="tw-text-sm tw-text-muted-foreground">
                Plans are listed on the{' '}
                <a href="/subscription" className="tw-text-primary">
                    subscription page
                </a>
                .
            </p>
        )
    }
    return (
        <div className="tw-grid tw-grid-cols-1 tw-gap-3 sm:tw-grid-cols-3">
            {options.map(option => {
                const selected = option.key === selectedKey
                const isCurrent = currentPlanId === option.planId
                return (
                    <button
                        key={option.key}
                        type="button"
                        onClick={() => onSelect(option.key)}
                        aria-pressed={selected}
                        data-testid={`plan-card-${option.key}`}
                        className={`tw-flex tw-cursor-pointer tw-flex-col tw-items-start tw-gap-2 tw-rounded-lg tw-border-2 tw-border-solid tw-bg-card tw-p-4 tw-text-left tw-text-foreground ${
                            selected ? 'tw-border-primary' : 'tw-border-border'
                        }`}
                    >
                        <span className="tw-text-sm tw-font-semibold tw-uppercase tw-tracking-wide tw-text-muted-foreground">
                            {option.label}
                            {isCurrent ? (
                                <span
                                    className="tw-ml-2 tw-rounded tw-bg-muted tw-px-1.5 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-normal-case tw-tracking-normal tw-text-foreground"
                                    data-testid="plan-card-current"
                                >
                                    Current
                                </span>
                            ) : null}
                        </span>
                        <span className="tw-text-2xl tw-font-semibold">
                            €{option.amount}
                            <span className="tw-text-sm tw-font-normal tw-text-muted-foreground">
                                {period === 'annual'
                                    ? ' /mo, billed yearly'
                                    : ' /month'}
                            </span>
                        </span>
                        <ul className="tw-m-0 tw-list-disc tw-pl-4 tw-text-sm tw-text-muted-foreground">
                            {option.bullets.map(bullet => (
                                <li key={bullet}>{bullet}</li>
                            ))}
                        </ul>
                    </button>
                )
            })}
        </div>
    )
}
